import React, { useState, useEffect, useRef, useCallback, useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable } from 'primereact/datatable'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { Row } from 'primereact/row'
import { ColumnGroup } from 'primereact/columngroup'

import MainContentLayout from '../../Layout/MainContentLayout'
import {
  formatCurrency,
  formatDate,
  formatPercentage,
} from '../../shared/utils'
import server from '../../../server'
import {
  DateFilterTemplate,
  DropDownTemplate,
  InputFieldTemplate,
  MinMaxTemplate,
} from '../../shared/FilterTemplates'

// Import system logos
import originsManganegment from '../../../assets/images/ORIGINS-MANAGEMENT.png'
import originsDirect from '../../../assets/images/ORIGINS-DIRECT.png'
import originsDirectInstant from '../../../assets/images/ORIGINS-DIRECT-INSTANT.png'
import openRxLogoPng from '../../../assets/images/open-rx-logo-png.png'
import { useBranding } from '../../../contexts/BrandingContext'
import { AuthenticationContext } from '../../../Auth/authentication.context'
import useTableRowsPerPage from '../../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../../shared/tablePagination'
//theme
import 'primereact/resources/themes/lara-light-indigo/theme.css'
//core
import 'primereact/resources/primereact.min.css'
//icons
import 'primeicons/primeicons.css'

const OriginsManagementOrders = () => {
  const dt = useRef()
  const { isGroupMode, logo, displayName } = useBranding()
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const [searchParams, setSearchParams] = useSearchParams()

  const currentDate = new Date()

  const [orders, setOrders] = useState()
  const [tableLoading, setTableLoading] = useState(true)
  const [totalRecords, setTotalRecords] = useState(0)
  const [csvLoading, setcsvLoading] = useState(false)
  const [zipLoading, setZipLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [lazyState, setLazyState] = useState(() => ({
    ...getInitialPaginationState(user),
    sortField: '',
    sortOrder: '',
    filters: {
      'supplier.name': { value: '' },
      supplierInvoiceNo: { value: '' },
      totalInvoicePa: { value: '' },
      totalInvoiceAmount: { value: '' },
      totalInvoicePaPercentage: { value: '' },
      status: { value: '' },
      orderDate: {
        value:
          Number(searchParams.get('today')) === 1
            ? [
                new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth(),
                  currentDate.getDate() - 1
                ),
                currentDate,
              ]
            : '',
      },
    },
  }))

  useEffect(() => {
    setLazyState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const navigate = useNavigate()

  const initLazyState = useCallback(() => {
    setLazyState((prevState) => ({
      ...prevState,
      sortField: '',
      sortOrder: '',
      filters: {
        'supplier.name': { value: '' },
        supplierInvoiceNo: { value: '' },
        status: { value: '' },
        totalInvoicePa: { value: '' },
        totalInvoiceAmount: { value: '' },
        totalInvoicePaPercentage: { value: '' },
        system: { value: '' },
        orderDate: {
          value:
            Number(searchParams.get('today')) === 1
              ? [
                  new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    currentDate.getDate() - 1
                  ),
                  currentDate,
                ]
              : '',
        },
      },
    }))
  }, [searchParams, currentDate])

  const onRowSelect = (event) => {
    const order = orders.find(
      (item) => item.supplierInvoiceNo === event.data.supplierInvoiceNo
    )
    navigate(`${event.data.id}`, { state: order })
  }

  const getData = useCallback(() => {
    let startDate
    let endDate
    const dates = lazyState?.filters?.orderDate?.value
    const totalAmount = lazyState?.filters?.totalInvoiceAmount?.value
    const supplier = lazyState?.filters?.['supplier.name']?.value
    const paAmount = lazyState?.filters?.totalInvoicePa?.value
    const orderNumber = lazyState?.filters?.supplierInvoiceNo?.value
    const status = lazyState?.filters?.status?.value
    const system = lazyState?.filters?.system?.value

    if (dates) {
      startDate = dates[0]
      endDate = dates[1] ? dates[1] : currentDate
    }

    const requestParams =
      `?page=${lazyState?.page ? lazyState.page + 1 : 1}` +
      `&limit=${lazyState?.rows}` +
      `${
        totalAmount?.min ? '&greaterThanTotalAmount=' + totalAmount.min : ''
      }` +
      `${totalAmount?.max ? '&lessThanTotalAmount=' + totalAmount.max : ''}` +
      `${paAmount?.min ? '&greaterThanPa=' + paAmount.min : ''}` +
      `${paAmount?.max ? '&lessThanPa=' + paAmount.max : ''}` +
      `${dates ? '&startDate=' + formatDate(startDate) : ''}` +
      `${dates ? '&endDate=' + formatDate(endDate) : ''}` +
      `${supplier ? '&supplierName=' + supplier.code : ''}` +
      `${status ? '&status=' + status : ''}` +
      `${system ? '&system=' + system : ''}` +
      `${orderNumber ? '&orderNo=' + orderNumber : ''}` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`

    setTableLoading(true)

    server
      .get(`/invoicesHeaders/pharmacy${requestParams}`, {
        headers: {
          'x-system-name': 'Origins Management',
        },
      })
      .then((response) => {
        const ordersData = response.data.invoicesHeaders
        setOrders(ordersData)
        setTotalRecords(response.data.totalCount)
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
      })
  }, [lazyState, currentDate])

  const exportCSV = () => {
    let startDate
    let endDate
    const dates = lazyState?.filters?.orderDate.value
    const totalAmount = lazyState?.filters?.totalInvoiceAmount.value
    const supplier = lazyState?.filters?.['supplier.name'].value
    const paAmount = lazyState?.filters?.totalInvoicePa.value
    const orderNumber = lazyState?.filters?.supplierInvoiceNo.value
    const status = lazyState?.filters?.status.value

    if (dates) {
      startDate = dates[0]
      endDate = dates[1] ? dates[1] : currentDate
    }

    const requestParams =
      '?' +
      `${
        totalAmount?.min ? '&greaterThanTotalAmount=' + totalAmount.min : ''
      }` +
      `${totalAmount?.max ? '&lessThanTotalAmount=' + totalAmount.max : ''}` +
      `${paAmount?.min ? '&greaterThanPa=' + paAmount.min : ''}` +
      `${paAmount?.max ? '&lessThanPa=' + paAmount.max : ''}` +
      `${dates ? '&startDate=' + formatDate(startDate) : ''}` +
      `${dates ? '&endDate=' + formatDate(endDate) : ''}` +
      `${supplier ? '&supplierName=' + supplier.code : ''}` +
      `${orderNumber ? '&orderNo=' + orderNumber : ''}` +
      `${status ? '&status=' + status : ''}` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`
    setcsvLoading(true)
    server
      .get(`/invoicesHeaders/exportOrders${requestParams}`, {
        headers: {
          'x-system-name': 'Origins Management',
        },
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'orders.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setcsvLoading(false)
      })
      .catch((error) => {
        setcsvLoading(false)
      })
  }

  const exportZip = () => {
    let startDate
    let endDate
    const dates = lazyState?.filters?.orderDate.value
    const totalAmount = lazyState?.filters?.totalInvoiceAmount.value
    const supplier = lazyState?.filters?.['supplier.name'].value
    const paAmount = lazyState?.filters?.totalInvoicePa.value
    const orderNumber = lazyState?.filters?.supplierInvoiceNo.value
    const status = lazyState?.filters?.status.value

    if (dates) {
      startDate = dates[0]
      endDate = dates[1] ? dates[1] : currentDate
    }

    const requestParams =
      '?' +
      `${totalAmount?.min ? '&greaterThanTotalAmount=' + totalAmount.min : ''}` +
      `${totalAmount?.max ? '&lessThanTotalAmount=' + totalAmount.max : ''}` +
      `${paAmount?.min ? '&greaterThanPa=' + paAmount.min : ''}` +
      `${paAmount?.max ? '&lessThanPa=' + paAmount.max : ''}` +
      `${dates ? '&startDate=' + formatDate(startDate) : ''}` +
      `${dates ? '&endDate=' + formatDate(endDate) : ''}` +
      `${supplier ? '&supplierName=' + supplier.code : ''}` +
      `${orderNumber ? '&orderNo=' + orderNumber : ''}` +
      `${status ? '&status=' + status : ''}` +
      `${lazyState?.sortField ? '&sortByField=' + lazyState.sortField : ''}` +
      `${lazyState?.sortOrder ? '&sortByOrder=' + lazyState.sortOrder : ''}`

    setZipLoading(true)
    server
      .get(`/invoicesHeaders/exportOrdersZip${requestParams}`, {
        headers: {
          'x-system-name': 'Origins Management',
        },
        responseType: 'blob',
      })
      .then((response) => {
        const blobData = new Blob([response.data], { type: 'application/zip' })
        const url = window.URL.createObjectURL(blobData)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'orders_export.zip')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        setZipLoading(false)
      })
      .catch(() => {
        setZipLoading(false)
      })
  }

  // BODY TEMPLATES
  const statusBodyTemplate = (order) => {
    let statusColor
    let textColor
    switch (order.status) {
      case 'Cancelled':
        statusColor = '#EF4444'
        textColor = '#EF4444'
        break
      case 'Pending':
        statusColor = '#86888b'
        textColor = '#86888b'

        break
      case 'Received':
        statusColor = '#FFA500'
        textColor = '#FFA500'

        break
      case 'Confirmed':
        statusColor = '#22c55e'
        textColor = '#22c55e'
        break
      default:
        statusColor = '#86888b'
        textColor = '#86888b'
        break
    }

    return (
      <span
        className={`d-flex justify-content-center py-1 rounded-pill`}
        style={{ border: `1px solid ${statusColor}`, color: `${textColor}` }}
      >
        {order.status}
      </span>
    )
  }

  const orderTotalTemplate = (order) => formatCurrency(order.totalInvoiceAmount)
  const paAmountBodyTemplate = (order) => formatCurrency(order.totalInvoicePa)
  const orderDateTemplate = (order) => formatDate(order.orderDate)
  const paRatioTemplate = (order) =>
    formatPercentage(order.totalInvoicePaPercentage)
  const manufacturerCodeTemplate = (rowData) => rowData.manufacturerCode || ''

  //! NEW SYSTEM BODY TEMPLATE
  const systemBodyTemplate = (order) => {
    // Check if supplier is K&F/K+F - if so, return blank
    const supplierName = order.supplier?.name || ''
    if (supplierName && (
        supplierName.toLowerCase().includes('k&f') || 
        supplierName.toLowerCase().includes('k+f') ||
        supplierName.toLowerCase().includes('k and f') || 
        supplierName.toLowerCase().includes('kandf'))) {
      return <span></span>
    }
    if (order.supplier && order.supplier.name !== 'Origins Direct') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={isGroupMode ? (logo || openRxLogoPng) : originsManganegment}
            alt={isGroupMode ? displayName : 'Origins Management'}
            style={{ height: '20px' }}
          />
        </div>
      )
    } else if (order.supplier && order.system === 'Origins Direct Instant') {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsDirectInstant}
            alt='Origins Direct Instant'
            style={{ height: '20px' }}
          />
        </div>
      )
    } else {
      return (
        <div className='d-flex justify-content-center'>
          <img
            src={originsDirect}
            alt='Origins Direct'
            style={{ height: '20px' }}
          />
        </div>
      )
    }
  }

  // Filter TEMPLATES
  const supplierFilterElement = (options) => (
    <DropDownTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select Supplier'
      options={[{ name: 'K+F', code: 'K%2BF' }]}
    />
  )

  const statusFilterElement = (options) => (
    <DropDownTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select Status'
      options={[
        { name: 'Confirmed', value: 'Confirmed' },
        { name: 'Pending', value: 'Pending' },
        { name: 'Received', value: 'Received' },
        { name: 'Cancelled', value: 'Cancelled' },
      ]}
    />
  )

  const orderTotalFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
    />
  )

  const paAmountFilterElement = (options) => (
    <MinMaxTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
    />
  )

  const orderDateFilterElement = (options) => (
    <DateFilterTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
    />
  )

  const orderNumberFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      placeholder='Order no.'
    />
  )

  const checkClearBtn = useCallback(() => {
    if (lazyState?.sortField || lazyState?.sortOrder) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in lazyState?.filters) {
      if (lazyState?.filters[key].value) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }, [lazyState])

  // TOOLBARS
  const leftToolbarTemplate = () => (
    <Button
      type='button'
      icon='pi pi-filter-slash'
      label='Clear'
      outlined
      onClick={() => {
        initLazyState()
        setSearchParams('')
      }}
      disabled={clearBtnDisabled}
    />
  )


  const onPage = (event) => {
    setLazyState((prev) => applyPageEvent(prev, event))
  }

  const onSort = (event) => {
    setLazyState(event)
  }

  const onFilter = (event) => {
    event.first = 0
    setLazyState(event)
  }

  useEffect(() => {
    initLazyState()
  }, [searchParams])

  useEffect(() => {
    checkClearBtn()
    getData()
  }, [lazyState])

  // Add system filter element
  const systemFilterElement = (options) => (
    <DropDownTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e.value, options.index)}
      placeholder='Select System'
      options={[
        { name: 'Origins Direct', value: 'OriginsDirect' },
        { name: 'Origins Direct Instant', value: 'OriginsDirectInstant' },
    
      ]}
    />
  )

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          header='Supplier'
          field='supplier.name'
          rowSpan={2}
          sortable
          filter
          filterElement={supplierFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        {/* Hidden in group portals – remove condition to restore */}
        {!isGroupMode && <Column header='Manufacturer Code' rowSpan={2} field='manufacturerCode' />}
        <Column
          header='Order Number'
          rowSpan={2}
          sortable
          field='supplierInvoiceNo'
          filter
          filterElement={orderNumberFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Order Date'
          rowSpan={2}
          sortable
          field='orderDate'
          filter
          filterElement={orderDateFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        <Column
          header='Order Total'
          rowSpan={2}
          sortable
          field='totalInvoiceAmount'
          filter
          filterElement={orderTotalFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        {/* Hidden in group portals – remove condition to restore */}
        {!isGroupMode && (
          <Column
            header='System'
            rowSpan={2}
            sortable
            field='system'
            filter
            filterElement={systemFilterElement}
            body={systemBodyTemplate}
            showFilterOperator={false}
            showAddButton={false}
            showFilterMenuOptions={false}
          />
        )}
        <Column
          header='Status'
          rowSpan={2}
          sortable
          field='status'
          filter
          filterElement={statusFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        {/* Hidden in group portals: Single / Dual / Multi Source – remove condition to restore */}
        {!isGroupMode && <Column header='Single Source Molecules' colSpan={3} />}
        {!isGroupMode && <Column header='Dual Source Molecules' colSpan={3} />}
        {!isGroupMode && <Column header='Multi Source Molecules' colSpan={3} />}
        <Column
          header='Total PA'
          rowSpan={2}
          sortable
          field='totalInvoicePa'
          filter
          filterElement={paAmountFilterElement}
          showFilterOperator={false}
          showAddButton={false}
          showFilterMenuOptions={false}
        />
        {/* Hidden in group portals – remove condition to restore */}
        {!isGroupMode && (
          <Column
            header='PA Percentage'
            rowSpan={2}
            sortable
            field='totalInvoicePaPercentage'
          />
        )}
      </Row>

      <Row>
        {/* Hidden in group portals: Single / Dual / Multi Source sub-headers – remove condition to restore */}
        {!isGroupMode && (
          <Column
            header='Total Amount'
            field='molecules.singleSource.totalInvoiceAmount'
          />
        )}
        {!isGroupMode && (
          <Column
            header='PA Amount'
            field='molecules.singleSource.totalRefundedAmount'
          />
        )}
        {!isGroupMode && (
          <Column
            header='PA Percentage'
            field='molecules.singleSource.paPercentage'
          />
        )}
        {!isGroupMode && (
          <Column
            header='Total Amount'
            field='molecules.dualSource.totalInvoiceAmount'
          />
        )}
        {!isGroupMode && (
          <Column
            header='PA Amount'
            field='molecules.dualSource.totalRefundedAmount'
          />
        )}
        {!isGroupMode && (
          <Column
            header='PA Percentage'
            field='molecules.dualSource.paPercentage'
          />
        )}
        {!isGroupMode && (
          <Column
            header='Total Amount'
            field='molecules.multiSource.totalInvoiceAmount'
          />
        )}
        {!isGroupMode && (
          <Column
            header='PA Amount'
            field='molecules.multiSource.totalRefundedAmount'
          />
        )}
        {!isGroupMode && (
          <Column
            header='PA Percentage'
            field='molecules.multiSource.paPercentage'
          />
        )}
      </Row>
    </ColumnGroup>
  )

  return (
    <MainContentLayout
      title='Your Orders'
      dt={exportCSV}
      handleExport={true}
      loading={csvLoading}
      dtZip={exportZip}
      handleExportZip={true}
      zipLoading={zipLoading}
    >
      <Toolbar className='mb-2' left={leftToolbarTemplate} />
      <DataTable
        ref={dt}
        lazy
        headerColumnGroup={headerGroup}
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        totalRecords={totalRecords}
        paginator
        value={orders}
        selectionMode='single'
        selection={selectedOrder}
        onSelectionChange={(e) => setSelectedOrder(e.value)}
        dataKey='id'
        onRowSelect={onRowSelect}
        tableStyle={{ minWidth: '50rem' }}
        loading={tableLoading}
        filters={lazyState?.filters}
        sortField={lazyState?.sortField}
        sortOrder={lazyState?.sortOrder}
        rows={lazyState?.rows}
        first={lazyState?.first}
        onPage={onPage}
        onSort={onSort}
        onFilter={onFilter}
        removableSort
        showGridlines
      >
        <Column field='supplier.name' />
        {/* Hidden in group portals – remove condition to restore */}
        {!isGroupMode && <Column field='manufacturerCode' body={manufacturerCodeTemplate} />}
        <Column field='supplierInvoiceNo' />
        <Column field='orderDate' body={orderDateTemplate} />
        <Column field='totalInvoiceAmount' body={orderTotalTemplate} />
        {!isGroupMode && <Column field='system' body={systemBodyTemplate} />}
        <Column field='status' body={statusBodyTemplate} />
        {/* Hidden in group portals: Single / Dual / Multi Source – remove condition to restore */}
        {!isGroupMode && (
          <Column
            field='molecules.singleSource.totalInvoiceAmount'
            body={(order) =>
              formatCurrency(order.molecules.singleSource.totalInvoiceAmount)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.singleSource.totalRefundedAmount'
            body={(order) =>
              formatCurrency(order.molecules.singleSource.totalRefundedAmount)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.singleSource.paPercentage'
            body={(order) =>
              formatPercentage(order.molecules.singleSource.paPercentage)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.dualSource.totalInvoiceAmount'
            body={(order) =>
              formatCurrency(order.molecules.dualSource.totalInvoiceAmount)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.dualSource.totalRefundedAmount'
            body={(order) =>
              formatCurrency(order.molecules.dualSource.totalRefundedAmount)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.dualSource.paPercentage'
            body={(order) =>
              formatPercentage(order.molecules.dualSource.paPercentage)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.multiSource.totalInvoiceAmount'
            body={(order) =>
              formatCurrency(order.molecules.multiSource.totalInvoiceAmount)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.multiSource.totalRefundedAmount'
            body={(order) =>
              formatCurrency(order.molecules.multiSource.totalRefundedAmount)
            }
          />
        )}
        {!isGroupMode && (
          <Column
            field='molecules.multiSource.paPercentage'
            body={(order) =>
              formatPercentage(order.molecules.multiSource.paPercentage)
            }
          />
        )}
        <Column field='totalInvoicePa' body={paAmountBodyTemplate} />
        {!isGroupMode && <Column field='totalInvoicePaPercentage' body={paRatioTemplate} />}
      </DataTable>
    </MainContentLayout>
  )
}

export default OriginsManagementOrders
